import { useEffect, useState } from 'preact/hooks'

interface CurrencySelectOptionsHookProps {
  baseCurrency: string
  counterCurrency: string
  supportedCurrenciesPairs: string[]
}

const getSupportedCurrencies = (
  targetCurrency: string,
  oppositeCurrency: string,
  changedCurrency: 'base' | 'counter',
  supportedCurrenciesPairs: string[],
): string[] => {
  const normalizedCurrency = targetCurrency.toUpperCase()
  const currencyToExclude = oppositeCurrency?.toUpperCase()
  const isBaseCurrencyChanged = changedCurrency === 'base'

  // Filter pairs based on the changed currency (base/counter)
  const matchingPairs = supportedCurrenciesPairs.filter((pair) => {
    return isBaseCurrencyChanged ? pair.endsWith(normalizedCurrency) : pair.startsWith(normalizedCurrency)
  })

  // Extract the opposite currency from each pair
  const mappedCurrencies = matchingPairs.map((pair) => {
    return isBaseCurrencyChanged ? pair.slice(0, 3) : pair.slice(3)
  })

  // Remove the currently selected opposite currency from the options list
  const filteredCurrencies = mappedCurrencies.filter((currency) => currency !== currencyToExclude)

  // Remove duplicates
  const uniqueCurrencies = Array.from(new Set(filteredCurrencies))

  // Prepend the current currency if it's not already in the list
  if (!uniqueCurrencies.includes(normalizedCurrency)) {
    uniqueCurrencies.unshift(normalizedCurrency)
  }

  return uniqueCurrencies
}

export const useCurrencySelectOptions = ({
  baseCurrency,
  counterCurrency,
  supportedCurrenciesPairs,
}: CurrencySelectOptionsHookProps) => {
  const [baseCurrencyOptions, setbaseCurrencyOptions] = useState<string[]>([])
  const [counterCurrencyOptions, setcounterCurrencyOptions] = useState<string[]>([])

  useEffect(() => {
    if (!supportedCurrenciesPairs.length) return

    if (baseCurrency) {
      const counterOptions = getSupportedCurrencies(baseCurrency, counterCurrency, 'base', supportedCurrenciesPairs)
      setcounterCurrencyOptions(counterOptions)
    }

    if (counterCurrency) {
      const baseOptions = getSupportedCurrencies(counterCurrency, baseCurrency, 'counter', supportedCurrenciesPairs)
      setbaseCurrencyOptions(baseOptions)
    }
  }, [baseCurrency, counterCurrency, supportedCurrenciesPairs])

  return { baseCurrencyOptions, counterCurrencyOptions }
}
